import { getCompatibleAuthTokenHttp } from 'src/services/utils/enhancedAuthTokenHttp';

export const GUIDELINE_BUDDY_VENDOR = 'GuidelineBuddy' as const;

export type GuidelineBuddyVendor = typeof GUIDELINE_BUDDY_VENDOR;

export type AiChatMode =
  | 'auto'
  | 'full_snapshot'
  | 'incremental_update'
  | 'query_only';

export interface SendGuidelineBuddyMessageParams {
  loanId: string;
  message: string;
  mode?: AiChatMode;
  force?: boolean;
}

export interface SendGuidelineBuddyMessageResult {
  loanId: string;
  vendor: GuidelineBuddyVendor;
  sessionId: string;
  createdSession: boolean;
  payloadMode: AiChatMode;
  snapshotVersion: number | null;
  previousSnapshotVersion: number | null;
  finalAnswer: string;
  events: unknown[];
  messageId?: string;
}

export interface InitializeGuidelineBuddyChatResult
  extends SendGuidelineBuddyMessageResult {
  reused: boolean;
}

export interface GuidelineBuddySessionResult {
  loanId: string;
  vendor: GuidelineBuddyVendor;
  sessionId: string;
  title: string | null;
  status: string;
  lastSnapshotVersionSent: number | null;
  lastSentAt: string | null;
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, unknown> | null;
  history: unknown;
  historyError?: string;
}

export interface GuidelineBuddyLoanDataRefresh {
  eventId: string;
  reason: 'DOCUMENT_CLASSIFICATION_COMPLETED' | 'MAPPED_FIELDS_CHANGED';
  occurredAt: string;
  documentIds?: string[];
  fieldIds?: string[];
}

export interface ResetGuidelineBuddySessionResult {
  loanId: string;
  vendor: GuidelineBuddyVendor;
  reset: boolean;
}

export interface GuidelineBuddyPreferences {
  guidelineBuddyVerificationToken?: string | null;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export const sendGuidelineBuddyMessage = async ({
  loanId,
  message,
  mode = 'auto',
  force = false,
}: SendGuidelineBuddyMessageParams): Promise<SendGuidelineBuddyMessageResult> => {
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<
    ApiEnvelope<SendGuidelineBuddyMessageResult>
  >(`/loans/${loanId}/ai-chat/messages`, {
    method: 'POST',
    body: {
      vendor: GUIDELINE_BUDDY_VENDOR,
      message,
      mode,
      force,
    },
  });

  if (response.data?.data) return response.data.data;
  throw new Error('Failed to send Guideline Buddy message');
};

const pendingInitializations = new Map<
  string,
  Promise<InitializeGuidelineBuddyChatResult>
>();

const requestGuidelineBuddyInitialization = async ({
  loanId,
  force = false,
}: {
  loanId: string;
  force?: boolean;
}): Promise<InitializeGuidelineBuddyChatResult> => {
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<
    ApiEnvelope<InitializeGuidelineBuddyChatResult>
  >(`/loans/${loanId}/ai-chat/initialize`, {
    method: 'POST',
    body: {
      vendor: GUIDELINE_BUDDY_VENDOR,
      force,
    },
  });

  if (response.data?.data) return response.data.data;
  throw new Error('Failed to initialize Guideline Buddy chat');
};

export const initializeGuidelineBuddyChat = ({
  loanId,
  force = false,
}: {
  loanId: string;
  force?: boolean;
}): Promise<InitializeGuidelineBuddyChatResult> => {
  const key = `${loanId}:${force ? 'force' : 'normal'}`;
  const pending = pendingInitializations.get(key);
  if (pending) return pending;

  const initialization = requestGuidelineBuddyInitialization({ loanId, force });
  pendingInitializations.set(key, initialization);
  const clearPending = () => {
    if (pendingInitializations.get(key) === initialization) {
      pendingInitializations.delete(key);
    }
  };
  void initialization.then(clearPending, clearPending);
  return initialization;
};

export const getGuidelineBuddySession = async ({
  loanId,
}: {
  loanId: string;
}): Promise<GuidelineBuddySessionResult | null> => {
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<
    ApiEnvelope<GuidelineBuddySessionResult | null>
  >(`/loans/${loanId}/ai-chat/session?vendor=${GUIDELINE_BUDDY_VENDOR}`, {
    method: 'GET',
  });

  if (response.data) return response.data.data;
  throw new Error('Failed to load Guideline Buddy session');
};

export const acknowledgeGuidelineBuddyRefresh = async ({
  loanId,
  eventId,
}: {
  loanId: string;
  eventId: string;
}): Promise<boolean> => {
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<ApiEnvelope<{ acknowledged: boolean }>>(
    `/loans/${loanId}/ai-chat/refresh/acknowledge`,
    {
      method: 'POST',
      body: { vendor: GUIDELINE_BUDDY_VENDOR, eventId },
    },
  );
  return Boolean(response.data?.data?.acknowledged);
};

export const resetGuidelineBuddySession = async ({
  loanId,
}: {
  loanId: string;
}): Promise<ResetGuidelineBuddySessionResult> => {
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<
    ApiEnvelope<ResetGuidelineBuddySessionResult>
  >(`/loans/${loanId}/ai-chat/session`, {
    method: 'DELETE',
  });

  if (response.data?.data) return response.data.data;
  throw new Error('Failed to reset Guideline Buddy session');
};

export const getGuidelineBuddyPreferences =
  async (): Promise<GuidelineBuddyPreferences> => {
    const authTokenHttp = await getCompatibleAuthTokenHttp();
    const response = await authTokenHttp<
      ApiEnvelope<GuidelineBuddyPreferences>
    >('/shared/user/preferences', {
      method: 'GET',
    });

    if (response.data?.data) return response.data.data;
    throw new Error('Failed to load Guideline Buddy preferences');
  };

export const saveGuidelineBuddyVerificationToken = async (
  guidelineBuddyVerificationToken: string | null,
): Promise<GuidelineBuddyPreferences> => {
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<ApiEnvelope<GuidelineBuddyPreferences>>(
    '/shared/user/preferences',
    {
      method: 'POST',
      body: {
        guidelineBuddyVerificationToken,
      },
    },
  );

  if (response.data?.data) return response.data.data;
  throw new Error('Failed to save Guideline Buddy verification token');
};
